import { Injectable } from '@angular/core';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { AppConfigurationClient } from '@azure/app-configuration';
import { environment } from '@env';
import { BehaviorSubject } from 'rxjs';
import { IConfiguration } from 'src/environments/ienvironment';
import { EnvironmentType } from '../../enums/general/environment.enum';

export function InitializeEnvironmentFactory(
    environmentService: EnvironmentService
) {
    return async (): Promise<void> => {
        return await environmentService.initialize();
    };
}

@Injectable({
    providedIn: 'root'
})
export class EnvironmentService {
    private _appConfigClient: AppConfigurationClient;
    private _environmentConfig = new BehaviorSubject<
        IConfiguration | undefined
    >(undefined);

    constructor(private authClient: AuthClientConfig) {
        this.loadAppConfigClient();
    }

    get environmentConfig(): IConfiguration {
        if (this._environmentConfig.value) {
            return this._environmentConfig.value;
        } else {
            throw new Error('Unable to load environment configuration');
        }
    }

    public initialize(): Promise<void> {
        return new Promise<void>(async resolve => {
            await this.loadEnvironmentConfig();
            await this.loadAuth0Client();
            resolve();
        });
    }

    private loadEnvironmentConfig(): Promise<void> {
        return new Promise<void>(async resolve => {
            if (environment.environmentType == EnvironmentType.Local) {
                this.loadLocalConfig();
            } else {
                await this.loadConfig();
            }
            resolve();
        });
    }

    private loadAuth0Client(): Promise<void> {
        return new Promise<void>(async resolve => {
            const auth0Config = this.environmentConfig.auth0;

            this.authClient.set({
                domain: auth0Config.domain,
                clientId: auth0Config.clientId,
                authorizationParams: {
                    audience: auth0Config.audience,
                    redirect_uri: auth0Config.callbackUrl
                }
            });

            resolve();
        });
    }

    private loadLocalConfig(): void {
        const config = environment.configuration;
        if (config) {
            this._environmentConfig.next(config);
            return;
        }
        throw new Error('Local configuration not found for local environment');
    }

    private async loadConfig(): Promise<void> {
        if (environment.configurationKey) {
            const config = (
                await this._appConfigClient.getConfigurationSetting({
                    key: environment.configurationKey
                })
            ).value;

            if (config) {
                this._environmentConfig.next(JSON.parse(config));
                return;
            }

            throw new Error(
                'Unable to load Dev/UAT/Prod environment configuration'
            );
        }
        throw new Error(
            'Unable to load Dev/UAT/Prod environment configuration'
        );
    }

    private loadAppConfigClient(): void {
        if (
            environment.environmentType == EnvironmentType.Development ||
            environment.environmentType == EnvironmentType.UAT ||
            environment.environmentType == EnvironmentType.Production
        ) {
            if (environment.appConfig) {
                this._appConfigClient = new AppConfigurationClient(
                    environment.appConfig
                );
                return;
            }
            throw new Error(
                'App configuration must be set in Development, UAT and Production environments'
            );
        } else {
            console.log(
                `${
                    EnvironmentType[environment.environmentType]
                } environment detected, defaulting to local configuration`
            );
        }
    }
}
